'use client'

import { useCallback } from 'react'
import { SnsPostXResponseVo } from '@schemas/non-auth'
import color from '@ui/style/color.theme'
import UIText from '@components/ui/UIText'
import UIFlex from '@components/ui/UIFlex'
import UIImage from '@components/ui/UIImage'
import pxToRem from '@utils/pxToRem'
import { Box } from '@mui/joy'
import parse from 'html-react-parser'
import UILink from '@components/ui/UILink'

interface UISnsListProps {
	snsPost: SnsPostXResponseVo
	hasChild?: boolean
}

const UISnsItem = ({ snsPost, hasChild = false }: UISnsListProps) => {
	const processContent = useCallback((content: string) => {
		const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(content)

		if (hasHtmlTags) {
			return parse(content)
		}
		return content
	}, [])

	return (
		<Box
			key={snsPost.snsId}
			sx={{
				position: 'relative',
				paddingLeft: {
					xs: pxToRem(40),
					md: pxToRem(28),
					lg: pxToRem(40),
				},
				...(hasChild && {
					'&::before': {
						content: '""',
						position: 'absolute',
						top: 0,
						left: { xs: pxToRem(19), md: pxToRem(13), lg: pxToRem(19) },
						width: '2px',
						height: 'calc(100% + 12px)',
						backgroundColor: '#D9D9D9',
						zIndex: -1,
					},
				}),
			}}
		>
			<UILink
				href="https://x.com/Jtbc_Factcheck"
				target="_blank"
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: { xs: pxToRem(40), md: pxToRem(28), lg: pxToRem(40) },
					height: { xs: pxToRem(40), md: pxToRem(28), lg: pxToRem(40) },
				}}
			>
				<UIImage
					ratio={1}
					src="/images/logo/logo_jtbc_sns.png"
					alt="JTBC Logo"
					sx={{ width: '100%', height: '100%' }}
				/>
			</UILink>
			<UIFlex
				flexDirection="column"
				alignItems="flex-start"
				gap={pxToRem(8)}
				sx={{ paddingLeft: { xs: pxToRem(8), md: pxToRem(12) } }}
			>
				<UIFlex
					flexDirection="row"
					alignItems={{ xs: 'flex-start', xl: 'center' }}
					justifyContent="space-between"
					sx={{ width: '100%' }}
				>
					<UIFlex flexDirection="row" alignItems={{ xs: 'flex-start', xl: 'center' }}>
						<UIFlex
							flexDirection={{ xs: 'column', xl: 'row' }}
							alignItems={{ xs: 'flex-start', xl: 'center' }}
							gap={{ xs: pxToRem(4), xl: pxToRem(8) }}
						>
							<UILink href="https://x.com/Jtbc_Factcheck" target="_blank">
								<UIText
									level="HeadingL"
									color={color.colBlack}
									lineHeight={{
										xs: pxToRem(22),
										xl: pxToRem(24),
									}}
									sx={{
										'&:hover': {
											color: color.colGray5,
										},
										fontSize: {
											xs: pxToRem(16),
											xl: pxToRem(17),
										},
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										width: {
											xs: '100%',
											md: pxToRem(120),
											lg: '100%',
										},
									}}
								>
									JTBC 대선 팩트체크
								</UIText>
							</UILink>
							<UIText
								color={color.colGray5}
								fontSize={pxToRem(13)}
								fontWeight={500}
								lineHeight={pxToRem(16)}
							>
								@Jtbc_Factcheck · {snsPost.createDateStr}
							</UIText>
						</UIFlex>
					</UIFlex>
					<UILink href={snsPost.snsUrl} target="_blank">
						<UIImage
							ratio={1}
							src="/images/logo/logo_x_sns.svg"
							alt="X 로고"
							sx={{
								width: pxToRem(18),
								height: pxToRem(18),
							}}
						/>
					</UILink>
				</UIFlex>
				<UIText
					color={color.colBlack}
					fontSize={{
						xs: pxToRem(14),
						xl: pxToRem(15),
					}}
					fontWeight={500}
					lineHeight={{
						xs: pxToRem(20),
						xl: pxToRem(22),
					}}
					sx={{
						textAlign: 'left',
					}}
				>
					{processContent(snsPost.content)}
				</UIText>
				{snsPost.attachmentList && snsPost.attachmentList.length > 0 && (
					<UILink
						href={snsPost.snsUrl}
						target="_blank"
						sx={{
							display: 'block',
							border: `1px solid ${color.colGray3}`,
							borderRadius: pxToRem(8),
							overflow: 'hidden',
							width: '100%',
							height: '100%',
						}}
					>
						{snsPost.attachmentList.length === 3 ? (
							<Box
								sx={{
									display: 'flex',
									height: {
										xs: pxToRem(150),
										md: pxToRem(88),
										lg: pxToRem(142),
										xl: pxToRem(228),
									},
								}}
							>
								<Box sx={{ flex: 1, marginRight: pxToRem(2) }}>
									<UIImage
										src={snsPost.attachmentList[0].url}
										alt=""
										style={{
											width: '100%',
											height: '100%',
											objectFit: 'cover',
										}}
									/>
								</Box>
								<Box
									sx={{
										flex: 1,
										display: 'flex',
										flexDirection: 'column',
										gap: pxToRem(2),
									}}
								>
									<UIImage
										src={snsPost.attachmentList[1].url}
										alt=""
										style={{
											width: '100%',
											height: '50%',
											objectFit: 'cover',
										}}
									/>
									<UIImage
										src={snsPost.attachmentList[2].url}
										alt=""
										style={{
											width: '100%',
											height: '50%',
											objectFit: 'cover',
										}}
									/>
								</Box>
							</Box>
						) : (
							<Box
								sx={{
									display: 'grid',
									gap: pxToRem(2),
									gridTemplateColumns:
										snsPost.attachmentList.length === 1 ? '1fr' : '1fr 1fr',
									gridTemplateRows:
										snsPost.attachmentList.length === 4 ? '1fr 1fr' : 'auto',
									gridTemplateAreas:
										snsPost.attachmentList.length === 4
											? `'img1 img2' 'img3 img4'`
											: undefined,
									height:
										snsPost.attachmentList.length === 1 ||
										snsPost.attachmentList.length === 4
											? '100%'
											: {
													xs: pxToRem(150),
													md: pxToRem(88),
													lg: pxToRem(142),
													xl: pxToRem(228),
											  },
								}}
							>
								{snsPost.attachmentList.map((item, idx) => {
									const area =
										snsPost.attachmentList.length === 4
											? `img${idx + 1}`
											: undefined
									return (
										<Box
											key={item.attachmentId}
											sx={{
												gridArea: area,
												width: '100%',
												height:
													snsPost.attachmentList.length === 4
														? {
																xs: pxToRem(74),
																md: pxToRem(43),
																lg: pxToRem(70),
																xl: pxToRem(113),
														  }
														: '100%',
											}}
										>
											<UIImage
												src={item.url}
												alt=""
												style={{
													width: '100%',
													height: '100%',
													objectFit: 'cover',
												}}
											/>
										</Box>
									)
								})}
							</Box>
						)}
					</UILink>
				)}
				{snsPost.replyCount > 0 && (
					<UILink href={snsPost.snsUrl} target="_blank">
						<UIFlex flexDirection="row" gap={pxToRem(12)}>
							<UIFlex flexDirection="row" gap={pxToRem(4)}>
								<UIImage
									ratio={1}
									src="/images/icon/iconReply.svg"
									sx={{
										width: pxToRem(18),
										height: pxToRem(18),
										marginLeft: 'auto',
										alignSelf: 'center',
									}}
								/>
								<UIText
									lineHeight={pxToRem(18)}
									fontSize={pxToRem(13)}
									color={color.colGray5}
								>
									{snsPost.replyCount}
								</UIText>
							</UIFlex>
						</UIFlex>
					</UILink>
				)}
			</UIFlex>
		</Box>
	)
}

export default UISnsItem
